// PROD-3310 billing bridge.
// Mirrors: dev-scrips-pm-react src/components/ui/dialog.tsx (base-ui compound Dialog: `Dialog` root with
// `open` / `onOpenChange`, `DialogContent` (`className`, `showCloseButton` default true: an X top-right),
// `DialogHeader`, `DialogTitle`, `DialogDescription`, `DialogFooter`, `DialogClose`, `DialogTrigger`).
// Wraps:   Signal DS `Modal` (prop-driven: `open` / `onClose` / `title` / `description` / `footer` / `size`).
// Overrides: the compound tree is LIFTED into Modal props: `DialogContent` walks its children (through
// `DialogHeader`) for the title and description, takes `DialogFooter`'s children as the Modal footer, and passes the
// rest as the body, so the accessible name is the DS Modal's own title. PM sizes dialogs with width classes; the
// ones PM's billing files pass map onto the DS width scale (`size`, see SIZE_FOR_CLASS), and `size` can be passed
// directly. The close X PM draws by default is a ghost icon button in the body's top-right corner. The overlay,
// radius, padding and shadow are the DS Modal's.
import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { X } from 'lucide-react';
import { Modal, type ModalSize } from '@scripsteam/scrips-signal-ds';
import { BridgeButton } from './button';

interface DialogState {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const DialogContext = createContext<DialogState | null>(null);

export function useBridgeDialog(): DialogState {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('Dialog parts must render inside <Dialog>.');
  return ctx;
}

export interface DialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: ReactNode;
}

export function Dialog({ open, defaultOpen = false, onOpenChange, children }: DialogProps) {
  const [inner, setInner] = useState(defaultOpen);
  const isOpen = open ?? inner;
  const setOpen = (next: boolean) => {
    if (open === undefined) setInner(next);
    onOpenChange?.(next);
  };
  return <DialogContext.Provider value={{ open: isOpen, setOpen }}>{children}</DialogContext.Provider>;
}

/** PM width classes -> the DS Modal width step nearest to them (DS-043: a value with no step moves onto the nearest). */
export const SIZE_FOR_CLASS: Readonly<Record<string, ModalSize>> = Object.freeze({
  'sm:max-w-sm': 'sm', // 384 -> 364
  'max-w-sm': 'sm',
  'sm:max-w-md': 'md', // 448 -> 480
  'max-w-md': 'md',
  'sm:max-w-lg': 'md', // 512 -> 480
  'max-w-lg': 'md',
  'max-w-xl': 'lg', // 576 -> 640
  'max-w-2xl': 'lg', // 672 -> 640
});

/** Split a PM className into the DS width step it names and the classes left over. */
export function sizeFromClassName(
  className: string | undefined,
  fallback: ModalSize,
): { size: ModalSize; rest: string | undefined } {
  let size: ModalSize = fallback;
  const rest: string[] = [];
  for (const token of (className ?? '').split(/\s+/).filter(Boolean)) {
    const mapped = SIZE_FOR_CLASS[token];
    if (mapped) size = mapped;
    else rest.push(token);
  }
  return { size, rest: rest.length ? rest.join(' ') : undefined };
}

export function DialogHeader({ children }: { children?: ReactNode; className?: string }) {
  return <>{children}</>;
}
export function DialogTitle({ children }: { children?: ReactNode; className?: string }) {
  return <>{children}</>;
}
export function DialogDescription({ children }: { children?: ReactNode; className?: string }) {
  return <>{children}</>;
}
export function DialogFooter({ children }: { children?: ReactNode; className?: string; showCloseButton?: boolean }) {
  return <>{children}</>;
}

export interface LiftedDialog {
  title?: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  body: ReactNode[];
}

/** Pull title / description (also from inside a header) and the footer out of a dialog body. */
export function liftDialogParts(
  children: ReactNode,
  parts: { header: unknown; title: unknown; description: unknown; footer: unknown },
  into: LiftedDialog = { body: [] },
): LiftedDialog {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      if (child != null && typeof child !== 'boolean') into.body.push(child);
      return;
    }
    const el = child as ReactElement<{ children?: ReactNode }>;
    if (el.type === parts.header) liftDialogParts(el.props.children, parts, into);
    else if (el.type === parts.title) into.title = el.props.children;
    else if (el.type === parts.description) into.description = el.props.children;
    else if (el.type === parts.footer) into.footer = el.props.children;
    else into.body.push(child);
  });
  return into;
}

const DIALOG_PARTS = {
  header: DialogHeader,
  title: DialogTitle,
  description: DialogDescription,
  footer: DialogFooter,
};

export interface DialogContentProps {
  className?: string;
  /** DS Modal width step; wins over a width class in `className`. */
  size?: ModalSize;
  showCloseButton?: boolean;
  children?: ReactNode;
}

export function DialogContent({ className, size, showCloseButton = true, children }: DialogContentProps) {
  const { open, setOpen } = useBridgeDialog();
  const mapped = sizeFromClassName(className, 'sm');
  const { title, description, footer, body } = liftDialogParts(children, DIALOG_PARTS);
  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title={title}
      description={description}
      footer={footer}
      size={size ?? mapped.size}
      className={mapped.rest}
    >
      {showCloseButton && (
        <BridgeButton
          variant="ghost"
          size="icon-sm"
          aria-label="Close"
          data-bridge-dialog-close=""
          className="absolute top-2 right-2"
          onClick={() => setOpen(false)}
        >
          <X aria-hidden />
        </BridgeButton>
      )}
      {body}
    </Modal>
  );
}

/** PM's `DialogClose`: closes the dialog; `render` lets the call site supply the element (base-ui shape). */
export function DialogClose({
  render,
  children,
  onClick,
  ...props
}: {
  render?: ReactElement<{ onClick?: (e: unknown) => void; children?: ReactNode }>;
  children?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}) {
  const { setOpen } = useBridgeDialog();
  const close = () => {
    onClick?.();
    setOpen(false);
  };
  if (render) return cloneElement(render, { onClick: close, children: children ?? render.props.children });
  return (
    <BridgeButton variant="outline" onClick={close} {...props}>
      {children}
    </BridgeButton>
  );
}

/** PM's `DialogTrigger`: opens the dialog. */
export function DialogTrigger({
  render,
  children,
}: {
  render?: ReactElement<{ onClick?: (e: unknown) => void; children?: ReactNode }>;
  children?: ReactNode;
}) {
  const { setOpen } = useBridgeDialog();
  if (render) return cloneElement(render, { onClick: () => setOpen(true), children: children ?? render.props.children });
  return (
    <BridgeButton variant="outline" onClick={() => setOpen(true)}>
      {children}
    </BridgeButton>
  );
}
